import PropTypes from 'prop-types';
import { extend, useDataModel } from '@thd-nucleus/data-sources';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { EMT_PAGE_NAMES, LAST_PATH, OPEN_GRAPH_FACEBOOK_ADMIN } from '../../constants';
import { filterEmtMetadataOverrides } from '../../helpers/emt-content-helpers';
import { generateStorePageCanonical, generateStorePageTitle } from '../../helpers/pps-helpers';
import { generateTags } from '../../utils/generate-tags';
import { emtContentModel, storeSearchModel } from '../../models';
import {
  generateRentalsDesc,
  getRentalsPreloadImages,
  getRentalsSchemas
} from './rentals-helpers';

// --- Tag Handler ---

const rentalsHandler = ({ data: { emtData, storeData }, props: { storeId } }) => {
  // Extract store details data once
  const storeInfo = {
    ...storeData?.storeSearch?.stores?.[0],
    storeId
  };
  const {
    name: storeName,
    address: { city, state, postalCode } = {},
    storeDetailsPageLink
  } = storeInfo;

  const canonicalURL = generateStorePageCanonical({
    storeDetailsPageLink, urlSuffix: LAST_PATH.RENTALS
  });

  // Extract emtContent data once
  const emtContent = emtData?.emtContent?.content || {};
  const emtContentMetadata = emtContent?.rentalsMetadata || {};
  const storeFinderPromotions = emtContent?.storeFinderPromotions || {};
  const emtContentFileData = emtContent?.[EMT_PAGE_NAMES.RENTALS] || {};

  const fallbackDescription = generateRentalsDesc({ storeName, city, state });
  const fallbackTitle = generateStorePageTitle({
    department: 'Tool and Truck Rental', storeName, city, state, postalCode
  });
  const {
    metaDescription = fallbackDescription,
    ogDescription = fallbackDescription,
    ogImage,
    ogTitle = fallbackTitle,
    ogType = 'PPS Rentals',
    schema: emtSchema,
    title = fallbackTitle
  } = filterEmtMetadataOverrides(emtContentMetadata);

  const preloadImages = getRentalsPreloadImages(storeFinderPromotions);

  const schemas = getRentalsSchemas({ emtContentFileData, emtSchema, storeInfo });

  const tags = generateTags({
    canonical: canonicalURL,
    description: metaDescription,
    fbAdmins: OPEN_GRAPH_FACEBOOK_ADMIN,
    ogDescription,
    ogImage,
    ogSiteName: 'The Home Depot',
    ogTitle,
    ogType,
    ogUrl: canonicalURL,
    preloadImages,
    schemas,
    title
  });

  return tags;
};

// --- Main Component ---

const RentalsMetadata = ({ storeId }) => {
  const emtContentOptions = {
    variables: {
      contentId: storeId,
      pageName: EMT_PAGE_NAMES.RENTALS
    }
  };
  const storeSearchOptions = {
    variables: {
      storeSearchInput: storeId,
      pagesize: '1'
    }
  };

  const { data: emtData } = useDataModel('emtContent', emtContentOptions);
  const { data: storeData } = useDataModel('storeSearch', storeSearchOptions);

  const data = { emtData, storeData };
  const props = { storeId };

  useHelmet('rentalsMetadata', { data, props, skipCleanup: true }, rentalsHandler, [storeId]);

  return null;
};

// --- Component Static Properties (dataModel, propTypes, etc.) ---

RentalsMetadata.displayName = 'RentalsMetadata';

RentalsMetadata.dataModel = extend(
  emtContentModel,
  storeSearchModel
);

RentalsMetadata.propTypes = {
  storeId: PropTypes.string.isRequired
};

export { RentalsMetadata };